/**
 * Datos "semilla" de un tenant nuevo, compartidos entre el alta de tenant desde el panel
 * admin (`src/components/forms/TenantForm`, via GraphQL) y el auto-registro publico
 * (`amplify/auth/post-confirmation`, escritura directa a DynamoDB). Modulo puro: no depende
 * de React, del SDK de AWS ni de Node (`crypto`/`Buffer`), solo arma objetos de datos — cada
 * caller decide como los persiste y les agrega `id`/`owner`/timestamps.
 */

export type ClientSeedData = {
  codigo: string;
  contacto: string;
  descuento: number;
  direccion: string;
  einvoice_kind: string;
  identificacion: string;
  nombre: string;
  priceId: string;
  tipo_identificacion: string;
  telefono1_cd: string;
  telefono1: string;
  email: string;
  is_default: boolean;
  tipo: number;
};

export const buildDefaultClient = (): ClientSeedData => ({
  codigo: "0",
  contacto: "Cliente Contado",
  descuento: 0,
  direccion: "Cliente Contado",
  einvoice_kind: "0",
  identificacion: "1111111",
  nombre: "Cliente Contado",
  priceId: "1",
  tipo_identificacion: "01",
  telefono1_cd: "506",
  telefono1: "11111111",
  email: "a@a.com",
  is_default: true,
  tipo: 0,
});

export type CurrencySeedData = {
  codigo: number;
  currency_id: number;
  exchange_rate: number;
  is_default: boolean;
};

export const buildDefaultCurrency = (): CurrencySeedData => ({
  codigo: 1,
  currency_id: 1,
  exchange_rate: 1,
  is_default: true,
});

export type RoleSeedData = {
  name: string;
  description: string;
  rules: string;
  version: number;
};

/**
 * Rol "Administrador" con ACL vacio: el acceso total se resuelve por `disabled_acl: true`
 * en la Suscription (ver Suscription en cada caller), no por reglas explicitas de ruta. Asi
 * no hace falta sincronizar este rol cada vez que se agrega una ruta a PRIVATE_ROUTE_CATALOG.
 */
export const buildDefaultAdminRole = (): RoleSeedData => ({
  name: "Administrador",
  description: "Rol administrador con acceso total",
  rules: JSON.stringify({}),
  version: 1,
});

export type SuscriptionBrandingData = {
  nombre?: string;
  telefono1?: string;
  email?: string;
  /** URL publica ya resuelta (ver src/utils/emailAssets.ts), o undefined si no hay logo. */
  logoUrl?: string;
};

export type EmailTemplateKind =
  | "invoice_email"
  | "creditnote_email"
  | "debitnote_email"
  | "qoute_email"
  | "consignment_email"
  | "providerorder_email"
  | "recibe_email"
  | "settlement_email"
  | "clientstatus_email"
  | "order_email";

type EmailTemplateLabel = {
  label: string;
  /** Terminacion del participio ("generad" + genero): "a" para femenino, "o" para masculino. */
  genero: "a" | "o";
  hasXml: boolean;
};

const EMAIL_TEMPLATE_LABELS: Record<EmailTemplateKind, EmailTemplateLabel> = {
  invoice_email: { label: "factura electrónica", genero: "a", hasXml: true },
  creditnote_email: { label: "nota de crédito electrónica", genero: "a", hasXml: true },
  debitnote_email: { label: "nota de débito electrónica", genero: "a", hasXml: true },
  qoute_email: { label: "cotización", genero: "a", hasXml: false },
  consignment_email: { label: "consignación", genero: "a", hasXml: false },
  providerorder_email: { label: "orden de compra", genero: "a", hasXml: false },
  order_email: { label: "orden de compra", genero: "a", hasXml: false },
  recibe_email: { label: "recibo de dinero", genero: "o", hasXml: false },
  settlement_email: { label: "liquidación", genero: "a", hasXml: false },
  clientstatus_email: { label: "estado de cuenta", genero: "o", hasXml: false },
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const renderXmlBody = (label: string): string => `<p style="font-size: 15px; color: #444444; line-height: 1.6;">Le informamos que su ${label} ha sido generada exitosamente. Adjunto a este correo encontrará los siguientes documentos:</p>
<ul style="font-size: 15px; color: #444444;">
<li>Documento XML de la ${label}</li>
<li>Archivo PDF con el detalle de la ${label}</li>
<li>Respuesta de aceptación de Hacienda (cuando aplique)</li>
</ul>
<p style="font-size: 15px; color: #444444; line-height: 1.6;">Le recomendamos conservar estos archivos para su contabilidad. Si tiene alguna consulta, no dude en contactarnos.</p>`;

const renderGenericBody = (label: string, genero: "a" | "o"): string => `<p style="font-size: 15px; color: #444444; line-height: 1.6;">Le informamos que su ${label} ha sido generad${genero} exitosamente. Adjunto a este correo encontrará el documento correspondiente.</p>
<p style="font-size: 15px; color: #444444; line-height: 1.6;">Si tiene alguna consulta, no dude en contactarnos.</p>`;

const renderShell = (bodyHtml: string, branding: SuscriptionBrandingData): string => {
  const logoUrl = branding.logoUrl ? escapeHtml(branding.logoUrl) : "";
  const nombre = escapeHtml(branding.nombre || "");
  const telefono1 = escapeHtml(branding.telefono1 || "");
  const email = escapeHtml(branding.email || "");

  const headerLogoRow = logoUrl
    ? `<tr>
<td style="padding: 20px; background-color: #004aad;"><img style="max-width: 200px;" src="${logoUrl}" alt="Logo"></td>
</tr>
`
    : "";

  const footerLogoCell = logoUrl
    ? `<td style="padding-right: 15px; vertical-align: middle;"><img style="width: auto;" src="${logoUrl}" alt="" width="39" height="59"></td>
`
    : "";

  return `<table style="padding: 20px 0;" width="100%" cellspacing="0" cellpadding="0" bgcolor="#f5f5f5">
<tbody>
<tr>
<td align="center"><br>
<table style="border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;" width="600" cellspacing="0" cellpadding="0" bgcolor="#ffffff">
<tbody>
${headerLogoRow}<tr>
<td style="padding: 30px;">
${bodyHtml}
</td>
</tr>
</tbody>
</table>
</td>
</tr>
</tbody>
</table>
<table style="font-family: Arial, sans-serif; font-size: 14px; color: #333;" border="0" cellspacing="0" cellpadding="0">
<tbody>
<tr>
${footerLogoCell}<td style="vertical-align: middle; padding-left: 10px;"><span style="color: #000000;"><span style="font-size: 16px;"><strong>${nombre}</strong></span></span><br><span style="color: #555;">Tel: ${telefono1}</span><br>Email: <a style="color: #1a73e8; text-decoration: none;" href="mailto:${email}">${email}</a></td>
</tr>
</tbody>
</table>`;
};

export const buildDefaultEmailTemplate = (
  kind: EmailTemplateKind,
  branding: SuscriptionBrandingData,
): string => {
  const { label, genero, hasXml } = EMAIL_TEMPLATE_LABELS[kind];
  const bodyHtml = hasXml ? renderXmlBody(label) : renderGenericBody(label, genero);
  return renderShell(bodyHtml, branding);
};

export const buildAllDefaultEmailTemplates = (
  branding: SuscriptionBrandingData,
): Record<EmailTemplateKind, string> => {
  const kinds = Object.keys(EMAIL_TEMPLATE_LABELS) as EmailTemplateKind[];
  return kinds.reduce((acc, kind) => {
    acc[kind] = buildDefaultEmailTemplate(kind, branding);
    return acc;
  }, {} as Record<EmailTemplateKind, string>);
};
